import { AccountingCampaign } from "../gateway/AccountingCampaign/AccountingCampaign";

const BANNER_TONES = ["neutral", "alert", "brand"];
const HIGHLIGHT_TONES = ["danger", "warning", "neutral"];
const MAX_HIGHLIGHTS = 3;

const isHttpsUrl = (value) =>
    typeof value === "string" && /^https:\/\//i.test(value.trim());

const toText = (value) =>
    value === undefined || value === null ? "" : String(value).trim();

/** Sem `?`, `&` nem `#`: o endereço vai cru para um `mailto:` e não pode carregar parâmetros. */
const EMAIL = /^[^\s@?&#]+@[^\s@?&#]+\.[^\s@?&#]+$/;

const toPositiveNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
};

/**
 * Faixa da Visão Geral. Qualquer resposta que não seja uma campanha completa — `null`, 403 de
 * outra contabilidade, erro de rede, back fora do ar — vira `null`: a tela simplesmente não
 * desenha a faixa, sem aviso nenhum ao usuário.
 */
export class GetActiveAccountingCampaignUsecase {
    constructor() {
        this.AccountingCampaignGateway = new AccountingCampaign();
    }

    async execute({ accountingId = "" } = {}) {
        const payload = this.sanitizeInput({ accountingId });
        if (!payload.accountingId) return null;

        try {
            const response = await this.AccountingCampaignGateway.getActive(
                payload.accountingId
            );
            return this.toCampaign(response?.campaign);
        } catch (error) {
            return null;
        }
    }

    sanitizeInput({ accountingId = "" } = {}) {
        return { accountingId: toText(accountingId) || null };
    }

    toCampaign(campaign) {
        const banner = campaign?.banner;
        const detail = campaign?.detail;
        const cta = campaign?.cta;
        const isComplete =
            toText(campaign?.campaignId) &&
            toText(banner?.title) &&
            isHttpsUrl(detail?.imageUrl) &&
            toText(cta?.label) &&
            isHttpsUrl(cta?.url);
        if (!isComplete) return null;

        return {
            campaignId: toText(campaign.campaignId),
            banner: {
                tone: BANNER_TONES.includes(banner.tone)
                    ? banner.tone
                    : "neutral",
                title: toText(banner.title),
                description: toText(banner.description) || null,
                highlights: this.toHighlights(banner.highlights),
                thumbnailUrl: isHttpsUrl(banner.thumbnailUrl)
                    ? banner.thumbnailUrl.trim()
                    : null,
                actionLabel: toText(banner.actionLabel) || "Ver mais"
            },
            detail: {
                imageUrl: detail.imageUrl.trim(),
                imageAlt: toText(detail.imageAlt),
                width: toPositiveNumber(detail.width),
                height: toPositiveNumber(detail.height)
            },
            cta: {
                label: toText(cta.label),
                url: cta.url.trim()
            },
            contact: this.toContact(campaign.contact)
        };
    }

    /**
     * Canais de contato do "Ver mais". Canal incompleto sai; sem nenhum válido, `null` — e o
     * diálogo volta a mostrar só o botão (`cta`), como antes do campo existir.
     */
    toContact(contact) {
        if (!contact) return null;
        const { chat, whatsapp, email } = contact;
        const normalized = {
            chat: toText(chat?.label) ? { label: toText(chat.label) } : null,
            whatsapp:
                toText(whatsapp?.label) && isHttpsUrl(whatsapp?.url) && toText(whatsapp?.display)
                    ? {
                          label: toText(whatsapp.label),
                          url: whatsapp.url.trim(),
                          display: toText(whatsapp.display)
                      }
                    : null,
            email:
                toText(email?.label) && EMAIL.test(toText(email?.address))
                    ? {
                          label: toText(email.label),
                          address: toText(email.address),
                          subject: toText(email.subject) || null
                      }
                    : null
        };
        return normalized.chat || normalized.whatsapp || normalized.email ? normalized : null;
    }

    toHighlights(highlights) {
        if (!Array.isArray(highlights)) return [];
        return highlights
            .filter((highlight) => toText(highlight?.value))
            .slice(0, MAX_HIGHLIGHTS)
            .map((highlight) => ({
                value: toText(highlight.value),
                label: toText(highlight.label),
                tone: HIGHLIGHT_TONES.includes(highlight.tone)
                    ? highlight.tone
                    : "neutral"
            }));
    }
}
